// src/components/edit/EditPage.tsx
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FirebaseService } from '../../services/firebase';
import { registrationFormSchema } from '../../schemas/registration';
import { DEFAULT_VALUES, type RegistrationFormData } from '../../types/registration';
import type { AgencyConfig } from '../../types/agency';
import { VideoSelector } from '../register/VideoSelector';
import { ImageSelector } from '../register/ImageSelector';
import { ImageUploader } from '../register/ImageUploader';

// shadcn/ui components
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

// サブスクリプションステータスの表示用
interface SubscriptionInfo {
  status: string;
  cancelAtPeriodEnd: boolean;
  cancelAt: string | null;
}

export const EditPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const shortTokenId = searchParams.get('id');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agencyData, setAgencyData] = useState<AgencyConfig | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(true);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);

  // React Hook Form
  const form = useForm<RegistrationFormData>({
    resolver: zodResolver(registrationFormSchema) as any,
    defaultValues: DEFAULT_VALUES,
    mode: 'onBlur',
  });

  const { handleSubmit, reset } = form;

  useEffect(() => {
    if (!shortTokenId) {
      setError('トークンが指定されていません');
      setLoading(false);
      return;
    }

    validateAndLoadAgency();
  }, [shortTokenId]);

  const validateAndLoadAgency = async () => {
    if (!shortTokenId) return;

    try {
      setLoading(true);
      const result = await FirebaseService.validateEditToken(shortTokenId);

      if (result.success && result.agencyData) {
        setAgencyData(result.agencyData);
        setIsSubscriptionActive(result.isSubscriptionActive ?? true);

        // サブスクリプション情報を取得
        if (result.agencyData.agencyId) {
          const subStatus = await FirebaseService.getSubscriptionStatus(result.agencyData.agencyId);
          if (subStatus.success && subStatus.hasSubscription) {
            setSubscriptionInfo({
              status: subStatus.status || 'unknown',
              cancelAtPeriodEnd: subStatus.cancelAtPeriodEnd || false,
              cancelAt: subStatus.cancelAt || null,
            });
          }
        }

        // AgencyConfigをRegistrationFormDataに変換してフォームに設定
        const formData: RegistrationFormData = {
          basicInfo: {
            agencyId: result.agencyData.agencyId,
            agencyName: result.agencyData.agencyName,
            email: result.agencyData.email || '',
            phone: result.agencyData.phone || '',
          },
          lpCustomize: {
            heroVideoKey: (result.agencyData.lpConfig?.hero?.videoKey || 'mv_sp') as
              | 'mv_sp'
              | 'mv_video1'
              | 'mv_video2'
              | 'mv_video3'
              | 'mv_video4'
              | 'mv_video5',
            heroTitle: result.agencyData.lpConfig?.hero?.title || '',
            visionImageKey: (result.agencyData.lpConfig?.vision?.imageKey || 'vision_image1') as
              | 'vision_image1'
              | 'vision_image2'
              | 'vision_image3'
              | 'vision_image4'
              | 'vision_image5'
              | 'vision_image6',
          },
          profile: {
            name: result.agencyData.lpConfig?.profile?.name || '',
            bio: result.agencyData.lpConfig?.profile?.bio || '',
            photoUrl: result.agencyData.lpConfig?.profile?.photoUrl || '',
            socialLinks: {
              instagram: result.agencyData.lpConfig?.profile?.socialLinks?.instagram || '',
              twitter: result.agencyData.lpConfig?.profile?.socialLinks?.twitter || '',
              youtube: result.agencyData.lpConfig?.profile?.socialLinks?.youtube || '',
            },
          },
          branding: {
            lineUrl: result.agencyData.lpConfig?.contacts?.lineUrl || '',
            applicationUrl: result.agencyData.lpConfig?.contacts?.applicationUrl || '',
          },
        };

        reset(formData);
      } else {
        setError(result.error || '代理店情報の取得に失敗しました');
      }
    } catch (err) {
      setError('エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (formData: RegistrationFormData) => {
    if (!agencyData || !shortTokenId) return;

    // 解約済みの場合は編集不可
    if (!isSubscriptionActive) {
      alert('サブスクリプションが解約されているため、編集できません');
      return;
    }

    try {
      setIsSaving(true);

      // RegistrationFormDataをAgencyConfig更新データに変換
      const updateData: Partial<AgencyConfig> = {
        agencyName: formData.basicInfo.agencyName,
        email: formData.basicInfo.email,
        phone: formData.basicInfo.phone,
        lpConfig: {
          hero: {
            videoKey: formData.lpCustomize?.heroVideoKey || 'mv_sp',
            title: formData.lpCustomize?.heroTitle || '',
          },
          vision: {
            imageKey: formData.lpCustomize?.visionImageKey || 'vision_image1',
          },
          profile: {
            name: formData.profile?.name || '',
            bio: formData.profile?.bio || '',
            photoUrl: formData.profile?.photoUrl || '',
            socialLinks: {
              instagram: formData.profile?.socialLinks?.instagram || '',
              twitter: formData.profile?.socialLinks?.twitter || '',
              youtube: formData.profile?.socialLinks?.youtube || '',
            },
          },
          cta: {
            text: '共済シミュレーターはこちら',
            url: agencyData.lpConfig?.cta?.url || '',
          },
          contacts: {
            lineUrl: formData.branding?.lineUrl || '',
            applicationUrl: formData.branding?.applicationUrl || '',
          },
        },
        branding: {
          logoUrl: '',
        },
      };

      const result = await FirebaseService.updateAgency(agencyData.agencyId, updateData);

      if (result.success) {
        alert('登録情報を更新しました！');
        await validateAndLoadAgency();
      } else {
        alert(`更新に失敗しました: ${result.error}`);
      }
    } catch (err) {
      alert('更新中にエラーが発生しました');
    } finally {
      setIsSaving(false);
    }
  };

  // Stripe Customer Portalを開く
  const openCustomerPortal = async () => {
    if (!agencyData) return;

    try {
      setIsLoadingPortal(true);
      const result = await FirebaseService.createCustomerPortalSession(
        agencyData.agencyId,
        shortTokenId || undefined
      );

      if (result.success && result.url) {
        // 新しいタブでStripe Customer Portalを開く
        window.open(result.url, '_blank');
      } else {
        alert(`エラー: ${result.error || 'ポータルを開けませんでした'}`);
      }
    } catch (err) {
      alert('エラーが発生しました');
    } finally {
      setIsLoadingPortal(false);
    }
  };

  // サブスクリプションステータスの表示
  const getSubscriptionStatusDisplay = () => {
    if (!subscriptionInfo) return null;

    const { status, cancelAtPeriodEnd, cancelAt } = subscriptionInfo;

    if (status === 'canceled') {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              解約済み
            </span>
          </div>
          <p className="text-sm text-red-700">
            サブスクリプションは解約されました。再度ご利用いただく場合は、新規登録をお願いします。
          </p>
        </div>
      );
    }

    if (cancelAtPeriodEnd && cancelAt) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              解約予約中
            </span>
          </div>
          <p className="text-sm text-yellow-700">
            {cancelAt}に解約予定です。それまではサービスをご利用いただけます。
          </p>
          <p className="text-xs text-yellow-600 mt-2">
            解約をキャンセルする場合は、下記のボタンから管理ページにアクセスしてください。
          </p>
        </div>
      );
    }

    if (status === 'active') {
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              有効
            </span>
          </div>
          <p className="text-sm text-green-700">サブスクリプションは有効です。</p>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込んでいます...</p>
        </div>
      </div>
    );
  }

  if (error || !agencyData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-red-800 mb-2">アクセスエラー</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => navigate('/register')} variant="default">
              新規登録ページへ
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 解約済みの場合の表示
  if (!isSubscriptionActive) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto py-8 px-4">
          {/* ヘッダー */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">登録情報</h1>
            <p className="text-sm text-gray-600">
              代理店ID: <span className="font-mono font-semibold">{agencyData.agencyId}</span>
            </p>
          </div>

          {/* 解約済みメッセージ */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <div className="text-red-600 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-red-800 mb-2">サブスクリプションが解約されています</h2>
              <p className="text-red-600 mb-6">
                代理店専用ページとシミュレーターへのアクセスは停止されています。
              </p>
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  再度サービスをご利用いただく場合は、新規登録をお願いします。
                  <br />
                  同じ代理店IDでの再登録が可能です。
                </p>
                <Button onClick={() => navigate('/register')} variant="default" className="mt-4">
                  新規登録ページへ
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* ヘッダー */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">登録情報の編集</h1>
          <p className="text-sm text-gray-600">
            代理店ID: <span className="font-mono font-semibold">{agencyData.agencyId}</span>
          </p>
        </div>

        {/* サブスクリプション管理セクション */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 border-b pb-2 mb-4">サブスクリプション管理</h2>

          {/* ステータス表示 */}
          <div className="mb-4">{getSubscriptionStatusDisplay()}</div>

          {/* 管理ボタン */}
          <div className="flex gap-3">
            <Button
              onClick={openCustomerPortal}
              disabled={isLoadingPortal}
              variant="outline"
              className="flex items-center gap-2"
            >
              {isLoadingPortal ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                  読み込み中...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  請求・解約の管理
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            ※ 解約、支払い方法の変更、請求履歴の確認はStripeの管理ページで行えます
          </p>
        </div>

        {/* フォーム */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              {/* Step 1: 基本情報 */}
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 border-b pb-2">基本情報</h2>

                <FormField
                  control={form.control}
                  name="basicInfo.agencyId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>代理店ID</FormLabel>
                      <FormControl>
                        <Input {...field} disabled className="bg-gray-100 cursor-not-allowed" />
                      </FormControl>
                      <FormDescription>代理店IDは変更できません</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="basicInfo.agencyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>名前</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="例: 山田 太郎" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="basicInfo.email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>メールアドレス</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" placeholder="例: contact@example.com" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="basicInfo.phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>電話番号</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="例: 03-1234-5678" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Step 2: LP カスタマイズ */}
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 border-b pb-2">LP カスタマイズ</h2>

                <FormField
                  control={form.control}
                  name="lpCustomize.heroVideoKey"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ヒーロー動画</FormLabel>
                      <FormControl>
                        <VideoSelector selectedVideo={field.value} onSelectVideo={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lpCustomize.heroTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ヒーロータイトル</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="メインキャッチコピーを入力（改行可能）" rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lpCustomize.visionImageKey"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ビジョン画像</FormLabel>
                      <FormControl>
                        <ImageSelector selectedImage={field.value} onSelectImage={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Step 3: プロフィール */}
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 border-b pb-2">プロフィール</h2>

                <FormField
                  control={form.control}
                  name="profile.name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>お名前</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="例: 山田 太郎" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="profile.photoUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>プロフィール画像</FormLabel>
                      <FormControl>
                        <ImageUploader currentImageUrl={field.value} onImageUploaded={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="profile.bio"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>自己紹介</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="あなたの経歴や想いを書いてください" rows={6} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="profile.socialLinks.instagram"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Instagram URL</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="https://instagram.com/your-account" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="profile.socialLinks.twitter"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>X (Twitter) URL</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="https://x.com/your-account" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="profile.socialLinks.youtube"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>YouTube URL</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="https://youtube.com/@your-channel" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Step 4: シミュレーターページ設定 */}
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 border-b pb-2">シミュレーターページ設定</h2>

                <FormField
                  control={form.control}
                  name="branding.lineUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>LINE URL</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="https://line.me/ti/p/xxxxx" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="branding.applicationUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>あなたのマインクラブへのリンク</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="https://example.com/apply" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* 送信ボタン */}
              <div className="flex justify-end gap-3 pt-6 border-t">
                <Button type="button" variant="outline" onClick={() => validateAndLoadAgency()} disabled={isSaving}>
                  リセット
                </Button>
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold border-2 border-blue-700 px-8 py-2.5 shadow-md"
                >
                  {isSaving ? '保存中...' : '保存'}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
};
